import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { SolarCalculator } from './components/SolarCalculator';
import { SeoContent } from './components/SeoContent';
import { Impressum, Haftungsausschluss } from './components/Legal';

// Scroll to top helper
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md py-4' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2 group">
          <div className="w-10 h-10 bg-solar-500 rounded-full flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <span className={`text-xl font-bold tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'} drop-shadow-sm`}>
            photovoltaik<span className="text-solar-500">günstig</span>.de
          </span>
        </Link>
        <div className="hidden md:flex space-x-8">
          <a href="/#rechner" className={`font-medium hover:text-solar-500 transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Rechner</a>
          <a href="/#vorteile" className={`font-medium hover:text-solar-500 transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Vorteile</a>
          <Link to="/impressum" className={`font-medium hover:text-solar-500 transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Kontakt</Link>
        </div>
        <a href="/#rechner" className="bg-solar-500 hover:bg-solar-600 text-white px-6 py-2 rounded-full font-bold shadow-lg transform hover:-translate-y-0.5 transition-all">
          Angebot anfordern
        </a>
      </div>
    </nav>
  );
};

const Footer: React.FC = () => (
  <footer className="bg-gray-900 text-gray-300 py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div>
        <h3 className="text-white text-lg font-bold mb-4">Über Uns</h3>
        <p className="text-sm leading-relaxed">
          photovoltaikgünstig.de wird betrieben von der track by track GmbH. Wir stehen für transparente Preise, deutsche Qualitätsstandards und innovative Energielösungen.
        </p>
      </div>
      <div>
        <h3 className="text-white text-lg font-bold mb-4">Schnelllinks</h3>
        <ul className="space-y-2 text-sm">
          <li><Link to="/" className="hover:text-solar-400">Startseite</Link></li>
          <li><Link to="/impressum" className="hover:text-solar-400">Impressum</Link></li>
          <li><Link to="/impressum" className="hover:text-solar-400">Datenschutz</Link></li>
          <li><Link to="/impressum" className="hover:text-solar-400">Haftungsausschluss</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="text-white text-lg font-bold mb-4">Kontakt</h3>
        <ul className="space-y-2 text-sm">
          <li>track by track GmbH</li>
          <li>Schliemannstr. 23, 10437 Berlin</li>
          <li>Tel: 030403665430</li>
          <li>Email: info@famefact.com</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-gray-800 mt-12 pt-8 text-center text-xs text-gray-500">
      &copy; {new Date().getFullYear()} track by track GmbH. Alle Rechte vorbehalten.
    </div>
  </footer>
);

const LandingPage: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <div className="relative bg-gray-900 overflow-hidden min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://picsum.photos/1920/1080?grayscale&blur=2" 
            alt="Photovoltaik Anlage Hintergrund" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center lg:text-left">
          <div className="lg:w-2/3">
            <span className="inline-block py-1 px-3 rounded-full bg-solar-500/20 text-solar-400 text-sm font-bold tracking-wide uppercase mb-6 border border-solar-500/30">
              Made for Germany
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight mb-8 leading-tight">
              Photovoltaik <span className="text-transparent bg-clip-text bg-gradient-to-r from-solar-400 to-yellow-200">günstig</span> & einfach.
            </h1>
            <p className="mt-4 text-xl text-gray-300 max-w-2xl mb-10 leading-relaxed">
              Sichern Sie sich Ihre Unabhängigkeit von steigenden Strompreisen. 
              Wir bieten hochleistungsfähige Solaranlagen und Speicher zum Bestpreis. 
              0% Mehrwertsteuer. 100% Zukunft.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#rechner" className="px-8 py-4 bg-solar-500 hover:bg-solar-600 text-white font-bold rounded-xl text-lg shadow-lg shadow-solar-500/30 transform transition hover:-translate-y-1">
                Sparpotenzial berechnen
              </a>
              <a href="#info" className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl text-lg backdrop-blur-sm transition border border-white/10">
                Mehr erfahren
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Signals */}
      <div className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center text-center opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
            {/* Placeholder Logos / Trust Badges */}
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-gray-800">25 Jahre</span>
              <span className="text-sm text-gray-500">Leistungsgarantie</span>
            </div>
             <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-gray-800">0% MwSt</span>
              <span className="text-sm text-gray-500">Nach § 12 Abs. 3 UStG</span>
            </div>
             <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-gray-800">TÜV</span>
              <span className="text-sm text-gray-500">Zertifizierte Module</span>
            </div>
             <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-gray-800">Express</span>
              <span className="text-sm text-gray-500">Lieferung möglich</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <SolarCalculator />

      {/* Content Section */}
      <div id="info">
        <SeoContent />
      </div>

      {/* FAQ / Schema Section (Hidden visually but structured for SEO, or displayed nicely) */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Häufige Fragen (FAQ)</h2>
          <div className="space-y-6">
            <details className="bg-white p-6 rounded-xl shadow-sm cursor-pointer group">
              <summary className="text-lg font-semibold list-none flex justify-between items-center text-gray-800 group-hover:text-solar-600 transition">
                Was kostet eine Photovoltaikanlage 2024?
                <span className="text-solar-500">+</span>
              </summary>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Die Preise sind stark gesunken. Eine typische Anlage mit 10 kWp inklusive Speicher und Montage kostet aktuell zwischen 15.000€ und 22.000€ (netto = brutto). Ohne Speicher ist es deutlich günstiger.
              </p>
            </details>
            <details className="bg-white p-6 rounded-xl shadow-sm cursor-pointer group">
              <summary className="text-lg font-semibold list-none flex justify-between items-center text-gray-800 group-hover:text-solar-600 transition">
                Wie lange hält eine Solaranlage?
                <span className="text-solar-500">+</span>
              </summary>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Moderne Solarmodule sind extrem langlebig. Die meisten Hersteller geben 25 bis 30 Jahre Leistungsgarantie. Die tatsächliche Lebensdauer kann sogar noch höher liegen.
              </p>
            </details>
             <details className="bg-white p-6 rounded-xl shadow-sm cursor-pointer group">
              <summary className="text-lg font-semibold list-none flex justify-between items-center text-gray-800 group-hover:text-solar-600 transition">
                Brauche ich eine Genehmigung?
                <span className="text-solar-500">+</span>
              </summary>
              <p className="mt-4 text-gray-600 leading-relaxed">
                In der Regel sind PV-Anlagen auf Privathäusern genehmigungsfrei. Ausnahmen können bei Denkmalschutz bestehen. Die Anlage muss jedoch beim Marktstammdatenregister und beim Netzbetreiber angemeldet werden.
              </p>
            </details>
          </div>
        </div>
      </div>
      
      {/* Structural Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Photovoltaik Komplettanlage inkl. Speicher",
          "image": "https://picsum.photos/1200/630",
          "description": "Hochleistungs-Photovoltaikanlage für Einfamilienhäuser. Günstig kaufen inklusive Speicher und Wechselrichter.",
          "brand": {
            "@type": "Brand",
            "name": "Track by Track Solar"
          },
          "offers": {
            "@type": "Offer",
            "url": "https://photovoltaikgünstig.de",
            "priceCurrency": "EUR",
            "price": "14999.00",
            "priceValidUntil": "2025-12-31",
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "124"
          }
        })}
      </script>
    </>
  );
};

const LegalPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-24">
      <div className="bg-white shadow-xl rounded-2xl p-8 md:p-12">
        <Impressum />
        <div className="border-t border-gray-200 my-8"></div>
        <Haftungsausschluss />
      </div>
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <div className="min-h-screen flex flex-col font-sans">
        <ScrollToTop />
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/impressum" element={<LegalPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </HashRouter>
  );
}

export default App;